import {
  Button,
  DataTable,
  type DataTableColumn,
  Modal,
} from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import {
  ArrowLeftIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { fetchAllCompanies } from "./components";
import { StatusForm } from "./status-form";

type Company = {
  id: number;
  name: string;
};

type Status = {
  id: string;
  companyId: number;
  valeurCompany: string;
  mappingChantierCarre: string | null;
};

export function CompanyStatusesPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const numericCompanyId = Number(companyId);

  const [company, setCompany] = useState<Company | null>(null);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | undefined>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingStatus, setDeletingStatus] = useState<Status | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchCompany = useCallback(async () => {
    const { companies } = await fetchAllCompanies();
    if (isNil(companies)) return;

    const found = companies.find((c) => c.id === numericCompanyId);
    if (found) {
      setCompany({
        id: found.id,
        name: found.name,
      });
    }
  }, [numericCompanyId]);

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiClient.GET(
      "/api/admin/companies/statuses",
      {
        params: { query: { companyId: numericCompanyId } },
      },
    );
    if (error) {
      setError("Erreur lors du chargement des statuts");
      setLoading(false);
      return;
    }

    const transformedStatuses = data.data.statuses.map((status) => ({
      ...status,
      id: String(status.id),
    }));

    setStatuses(transformedStatuses);
    setLoading(false);
  }, [numericCompanyId]);

  useEffect(() => {
    fetchCompany();
    fetchStatuses();
  }, [fetchCompany, fetchStatuses]);

  const handleCreate = () => {
    setEditingStatus(undefined);
    setDrawerOpen(true);
  };

  const handleEdit = (status: Status) => {
    setEditingStatus(status);
    setDrawerOpen(true);
  };

  const handleDelete = (status: Status) => {
    setDeletingStatus(status);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingStatus) return;

    setIsDeleting(true);
    const { error } = await apiClient.DELETE(
      "/api/admin/companies/statuses/{id}",
      {
        params: { path: { id: Number(deletingStatus.id) } },
      },
    );

    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la suppression du statut",
      });
      setIsDeleting(false);
      return;
    }

    toaster.success({
      title: "Succès",
      description: "Statut supprimé avec succès",
    });

    setDeleteModalOpen(false);
    setDeletingStatus(null);
    setIsDeleting(false);
    fetchStatuses();
  };

  const handleFormSuccess = () => {
    fetchStatuses();
  };

  const columns: DataTableColumn<Status>[] = [
    {
      id: "valeurCompany",
      header: "Valeur entreprise",
      accessor: "valeurCompany",
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "mappingChantierCarre",
      header: "Mapping ChantierCarré",
      accessor: (row) => row.mappingChantierCarre ?? "-",
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "actions",
      header: "Actions",
      accessor: () => "",
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button plain onClick={() => handleEdit(row)} aria-label="Modifier">
            <PencilIcon className="size-4" />
          </Button>
          <Button
            plain
            onClick={() => handleDelete(row)}
            aria-label="Supprimer"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex min-h-[200px] items-center justify-center">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            plain
            onClick={() => navigate("/admin/companies")}
            aria-label="Retour"
          >
            <ArrowLeftIcon className="size-5" />
          </Button>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Statuts de {company?.name ?? "..."}
          </h2>
        </div>
        <Button onClick={handleCreate}>
          <PlusIcon data-slot="icon" className="size-4" />
          Créer un statut
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={statuses}
        isLoading={loading}
        isEmpty={statuses.length === 0}
        emptyState={
          <div className="flex min-h-[200px] items-center justify-center text-gray-500">
            Aucun statut trouvé
          </div>
        }
      />

      {company && (
        <StatusForm
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          subscriberId={0}
          companyId={company.id}
          status={
            editingStatus
              ? {
                  id: Number(editingStatus.id),
                  valeurCompany: editingStatus.valeurCompany,
                  mappingChantierCarre: editingStatus.mappingChantierCarre,
                }
              : undefined
          }
          onSuccess={handleFormSuccess}
        />
      )}

      <Modal.Root
        open={deleteModalOpen}
        onOpenChange={(details) => setDeleteModalOpen(details.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Confirmer la suppression</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Êtes-vous sûr de vouloir supprimer le statut "
                  {deletingStatus?.valeurCompany}" ? Cette action est
                  irréversible.
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  Annuler
                </Button>
                <Button
                  color="red"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Suppression..." : "Supprimer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </div>
  );
}
